/**
 * Help Center main landing page
 * Displays all categories with articles, search, and quick actions
 */

import { useState, useEffect, useMemo } from 'react';
import {
  getHelpCategories,
  getHelpArticles,
  getPopularArticles,
  getQuickActions,
  groupArticlesByCategory,
  PopularArticle,
} from '../../services/helpContent';
import { HelpCategory, HelpArticleSummary, HelpQuickAction } from '../../types';
import {
  HelpSearch,
  HelpQuickActions,
  HelpCategorySidebar,
  HelpCategorySection,
  HelpCTA,
} from '../../components/help';

// Default quick actions if none configured
const defaultQuickActions: HelpQuickAction[] = [
  {
    id: 'contact',
    title: 'Contact Support',
    description: "Can't find what you're looking for?",
    icon: 'message-square',
    buttonText: 'Get in Touch',
    linkUrl: '/contact',
    order: 0,
  },
  {
    id: 'videos',
    title: 'Video Tutorials',
    description: 'Watch step-by-step guides',
    icon: 'play-circle',
    buttonText: 'Watch Now',
    linkUrl: '/tutorials',
    order: 1,
  },
  {
    id: 'guide',
    title: 'User Guide',
    description: 'Comprehensive platform guide',
    icon: 'book-open',
    buttonText: 'Download PDF',
    linkUrl: '/guide.pdf',
    order: 2,
  },
];

// Default categories if none configured
const defaultCategories: HelpCategory[] = [
  {
    id: 'getting-started',
    name: 'Getting Started',
    description: 'Learn the basics of using Foxie Systems',
    icon: 'book-open',
    order: 0,
  },
  {
    id: 'purchasing-downloads',
    name: 'Purchasing & Downloads',
    description: 'Everything about buying and accessing systems',
    icon: 'shopping-bag',
    order: 1,
  },
  {
    id: 'account-management',
    name: 'Account Management',
    description: 'Manage your profile and settings',
    icon: 'user',
    order: 2,
  },
  {
    id: 'billing-refunds',
    name: 'Billing & Refunds',
    description: 'Payment and refund information',
    icon: 'credit-card',
    order: 3,
  },
  {
    id: 'content-creators',
    name: 'For Content Creators',
    description: 'Information for system creators',
    icon: 'edit',
    order: 4,
  },
  {
    id: 'technical-support',
    name: 'Technical Support',
    description: 'Technical issues and troubleshooting',
    icon: 'wrench',
    order: 5,
  },
];

export function HelpCenterPage(): JSX.Element {
  const [categories, setCategories] = useState<HelpCategory[]>(defaultCategories);
  const [articles, setArticles] = useState<HelpArticleSummary[]>([]);
  const [popularArticles, setPopularArticles] = useState<PopularArticle[]>([]);
  const [quickActions, setQuickActions] = useState<HelpQuickAction[]>(defaultQuickActions);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load data
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const [cats, arts, popular, actions] = await Promise.all([
          getHelpCategories().catch(() => defaultCategories),
          getHelpArticles().catch(() => []),
          getPopularArticles().catch(() => []),
          getQuickActions().catch(() => defaultQuickActions),
        ]);

        setCategories(cats.length > 0 ? cats : defaultCategories);
        setArticles(arts);
        setPopularArticles(popular);
        setQuickActions(actions.length > 0 ? actions : defaultQuickActions);
        setError(null);
      } catch (err) {
        console.error('Error loading help content:', err);
        setError('Failed to load help content. Please try again later.');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  // Group articles by category
  const groupedArticles = useMemo(() => {
    return groupArticlesByCategory(articles, categories);
  }, [articles, categories]);

  // Category name lookup for search
  const getCategoryName = (categoryId: string): string => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  // Set page title
  useEffect(() => {
    document.title = 'Help Center - Foxie Systems';
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header Section */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="container-custom py-12 text-center">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              Help Center
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-xl mx-auto">
              Find answers to common questions, guides, and troubleshooting help
            </p>

            {/* Search */}
            <div className="max-w-xl mx-auto">
              <HelpSearch
                articles={articles}
                categories={categories}
                placeholder="Search for help articles..."
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container-custom py-10">
          {/* Quick Actions */}
          <HelpQuickActions actions={quickActions} />

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading help content...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 dark:text-red-400">{error}</p>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Sidebar */}
              <HelpCategorySidebar
                categories={categories}
                popularArticles={popularArticles}
                getCategoryName={getCategoryName}
              />

              {/* Main Content Area */}
              <main className="flex-1 min-w-0">
                {articles.length === 0 ? (
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
                    <p className="text-gray-600 dark:text-gray-400">
                      No help articles available yet. Check back soon!
                    </p>
                  </div>
                ) : (
                  Array.from(groupedArticles.values())
                    .filter(group => group.articles.length > 0)
                    .map(({ category, articles: categoryArticles }) => (
                      <HelpCategorySection
                        key={category.id}
                        category={category}
                        articles={categoryArticles}
                        maxArticles={4}
                        showViewAll
                      />
                    ))
                )}
              </main>
            </div>
          )}

          {/* CTA Section */}
          <HelpCTA />
        </div>
      </div>
  );
}

export default HelpCenterPage;
